import {
  QuestionAnalysisCountsDailyStat,
  TopicDistributionItem,
  RelevanceDailyStat,
  ActiveUserItem,
  DisciplineDistributionItem,
  IndustryDistributionItem,
  NonRelevantQuestionItem,
  WebSearchQuestionItem,
  MessageSearchResultItem,
} from './dashboardApi';

export interface CsvSpec {
  headers: string[];
  rows: Array<Array<string | number | null | undefined>>;
}

export const buildMessagesCsvSpec = (
  stats: QuestionAnalysisCountsDailyStat[]
): CsvSpec => ({
  headers: ['Date', 'Total Messages', 'Non-Relevant Messages', 'Web-Search Enabled Messages'],
  rows: stats.map((stat) => [
    stat.date ?? '',
    sanitizeNumber(stat.total_messages),
    sanitizeNumber(stat.non_related_messages),
    sanitizeNumber(stat.web_search_enabled_messages),
  ]),
});

export const buildTopicsCsvSpec = (
  topics: TopicDistributionItem[],
  othersCount?: number
): CsvSpec => {
  const rows = topics.map((topic) => [
    topic.name || `SDG ${topic.sdg_number}`,
    topic.sdg_number ?? '--',
    sanitizeNumber(topic.message_count),
  ]);
  if (othersCount !== undefined && othersCount > 0) {
    rows.push(['Others', '--', sanitizeNumber(othersCount)]);
  }
  return {
    headers: ['Topic', 'SDG', 'Messages'],
    rows,
  };
};

export const buildRelevanceCsvSpec = (
  stats: RelevanceDailyStat[]
): CsvSpec => ({
  headers: ['Date', 'Average Relevance Score', 'Question Count'],
  rows: stats.map((stat) => [
    stat.date ?? '',
    typeof stat.avg_relevance_score === 'number' ? stat.avg_relevance_score.toFixed(2) : '',
    sanitizeNumber(stat.question_count),
  ]),
});

export const buildActiveUsersCsvSpec = (users: ActiveUserItem[]): CsvSpec => ({
  headers: ['Rank', 'User', 'User ID', 'Messages'],
  rows: users.map((user) => [
    sanitizeNumber(user.rank),
    user.username ?? '',
    sanitizeNumber(user.user_id),
    sanitizeNumber(user.message_count),
  ]),
});

export const buildWordCloudCsvSpec = (
  topics: TopicDistributionItem[]
): CsvSpec => ({
  headers: ['Topic', 'Messages'],
  rows: topics.map((topic) => [
    topic.name || `SDG ${topic.sdg_number}`,
    sanitizeNumber(topic.message_count),
  ]),
});

export const buildDisciplinesCsvSpec = (
  disciplines: DisciplineDistributionItem[]
): CsvSpec => ({
  headers: ['Discipline', 'Messages'],
  rows: disciplines.map((item) => [
    item.name ?? '',
    sanitizeNumber(item.message_count),
  ]),
});

export const buildIndustriesCsvSpec = (
  industries: IndustryDistributionItem[]
): CsvSpec => ({
  headers: ['Industry', 'Messages'],
  rows: industries.map((item) => [
    item.name ?? '',
    sanitizeNumber(item.message_count),
  ]),
});

export const buildNonRelevantCsvSpec = (
  questions: NonRelevantQuestionItem[]
): CsvSpec => ({
  headers: ['Message ID', 'Question', 'Username', 'User ID', 'Time'],
  rows: questions.map((question) => [
    question.message_id ?? '',
    question.question ?? '',
    question.username ?? '',
    sanitizeNumber(question.user_id),
    question.question_timestamp ?? '',
  ]),
});

export const buildWebSearchCsvSpec = (
  questions: WebSearchQuestionItem[]
): CsvSpec => ({
  headers: ['Message ID', 'Question', 'Username', 'User ID', 'Time'],
  rows: questions.map((question) => [
    question.message_id ?? '',
    question.question ?? '',
    question.username ?? '',
    sanitizeNumber(question.user_id),
    question.question_timestamp ?? '',
  ]),
});

export const buildSearchResultsCsvSpec = (
  results: MessageSearchResultItem[],
  keyword: string
): CsvSpec => ({
  headers: ['Keyword', 'Message ID', 'Question', 'Answer', 'Username', 'User ID', 'Time'],
  rows: results.map((result) => [
    keyword,
    result.message_id ?? '',
    result.question ?? '',
    result.answer ?? '',
    result.username ?? '',
    sanitizeNumber(result.user_id),
    result.question_timestamp ?? '',
  ]),
});

const sanitizeNumber = (value?: number | null): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  return 0;
};
